/**
 * 登录模态框组件
 * 
 * 功能：
 * - 邮箱+密码登录
 * - 密码显示/隐藏切换
 * - 密码规则提示
 * - 忘记密码链接
 * - 加载状态
 * - 错误提示
 */

import { useState, useCallback, type FormEvent } from 'react'
import { Eye, EyeOff, Mail, Lock, AlertCircle } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import LoadingSpinner from '@/components/LoadingSpinner'
import { cn } from '@/lib/utils'

interface LoginModalProps {
  /** 是否允许关闭（默认不允许） */
  allowClose?: boolean
  /** 关闭回调 */
  onClose?: () => void
  /** 点击忘记密码回调 */
  onForgotPassword?: () => void
}

export const LoginModal = ({ 
  allowClose = false, 
  onClose,
  onForgotPassword 
}: LoginModalProps) => {
  const { signIn, resetPassword } = useAuth()
  
  // 表单状态
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordRules, setShowPasswordRules] = useState(false)
  
  // UI 状态
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  
  // 模式：login | forgot
  const [mode, setMode] = useState<'login' | 'forgot'>('login')

  // 验证邮箱格式
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // 处理登录
  const handleLogin = useCallback(async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccessMessage(null)

    // 验证
    if (!email.trim()) {
      setError('请输入邮箱地址')
      return
    }
    if (!isValidEmail(email)) {
      setError('请输入有效的邮箱地址')
      return
    }
    if (!password) {
      setError('请输入密码')
      return
    }
    if (password.length < 6) {
      setError('密码至少需要6个字符')
      return
    }

    setIsLoading(true)

    try {
      const { error: signInError } = await signIn(email, password)
      
      if (signInError) {
        setError(signInError.message)
      }
      // 登录成功会自动更新 AuthContext，不需要额外处理
    } catch (err) {
      setError('登录失败，请重试')
    } finally {
      setIsLoading(false)
    }
  }, [email, password, signIn])

  // 处理忘记密码
  const handleForgotPassword = useCallback(async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccessMessage(null)

    if (!email.trim()) {
      setError('请输入邮箱地址')
      return
    }
    if (!isValidEmail(email)) {
      setError('请输入有效的邮箱地址')
      return
    }

    setIsLoading(true)

    try {
      const { error: resetError } = await resetPassword(email)
      
      if (resetError) {
        setError(resetError.message)
      } else {
        setSuccessMessage('密码重置链接已发送到您的邮箱，请查收')
      }
    } catch (err) {
      setError('发送重置邮件失败，请重试')
    } finally {
      setIsLoading(false)
    }
  }, [email, resetPassword])

  // 切换到忘记密码模式
  const handleSwitchToForgot = () => {
    setMode('forgot')
    setError(null)
    setSuccessMessage(null)
    setPassword('')
    if (onForgotPassword) {
      onForgotPassword()
    }
  }

  // 切换回登录模式
  const handleSwitchToLogin = () => {
    setMode('login')
    setError(null)
    setSuccessMessage(null)
  }

  // 处理背景点击
  const handleBackdropClick = () => {
    if (allowClose && onClose) {
      onClose()
    }
  }

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-title"
    >
      {/* 模态框容器 */}
      <div 
        className="console-panel relative w-full max-w-md shadow-[0_24px_70px_rgba(0,0,0,.58)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 加载遮罩 */}
        {isLoading && <LoadingSpinner variant="modal" text="处理中..." />}
        
        <div className="p-8">
          {/* 标题 */}
          <div className="text-center mb-8">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg border border-[var(--border-control)] bg-[var(--panel-deep)]">
              <Lock className="h-8 w-8 text-[var(--action-blue)]" />
            </div>
            <h2 id="login-title" className="text-2xl font-bold text-[var(--text-ink)]">
              {mode === 'login' ? '登录' : '重置密码'}
            </h2>
            <p className="mt-2 text-sm text-[var(--text-muted)]">
              {mode === 'login' 
                ? 'B站直播分析平台' 
                : '输入您的邮箱，我们将发送重置链接'}
            </p>
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="console-alert-error mb-6 flex items-start gap-3 p-4">
              <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* 成功提示 */}
          {successMessage && (
            <div className="console-alert-success mb-6 p-4">
              <p className="text-sm">{successMessage}</p>
            </div>
          )}

          {/* 表单 */}
          <form onSubmit={mode === 'login' ? handleLogin : handleForgotPassword}>
            {/* 邮箱输入 */}
            <div className="mb-4">
              <Label htmlFor="email" className="text-sm font-medium text-[var(--text-soft)]">
                邮箱地址
              </Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[var(--text-muted)]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="请输入邮箱"
                  className="pl-10"
                  disabled={isLoading}
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            {/* 密码输入（仅登录模式） */}
            {mode === 'login' && (
              <div className="mb-4">
                <Label htmlFor="password" className="text-sm font-medium text-[var(--text-soft)]">
                  密码
                </Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[var(--text-muted)]" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="请输入密码"
                    className="pl-10 pr-10"
                    disabled={isLoading}
                    autoComplete="current-password"
                    required
                    onFocus={() => setShowPasswordRules(true)}
                    onBlur={() => setTimeout(() => setShowPasswordRules(false), 200)}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-soft)]"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? '隐藏密码' : '显示密码'}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                
                {/* 密码规则提示 */}
                <div 
                  className={cn(
                    "mt-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--panel-deep)] p-3 text-xs text-[var(--text-muted)] transition-all duration-200",
                    showPasswordRules ? "opacity-100 max-h-24" : "opacity-0 max-h-0 overflow-hidden p-0 mt-0 border-0"
                  )}
                >
                  <p className="font-medium mb-1">密码要求：</p>
                  <ul className="list-disc pl-4 space-y-0.5">
                    <li>至少 6 个字符</li>
                    <li>不允许特殊 Unicode 字符</li>
                  </ul>
                </div>
              </div>
            )}

            {/* 忘记密码链接（仅登录模式） */}
            {mode === 'login' && (
              <div className="mb-6 text-right">
                <button
                  type="button"
                  className="text-sm text-[var(--action-blue)] hover:text-[var(--text-ink)] focus:outline-none focus:underline"
                  onClick={handleSwitchToForgot}
                  disabled={isLoading}
                >
                  忘记密码？
                </button>
              </div>
            )}

            {/* 提交按钮 */}
            <Button
              type="submit"
              className="w-full h-11 text-base"
              disabled={isLoading}
            >
              {isLoading 
                ? '处理中...' 
                : (mode === 'login' ? '登录' : '发送重置链接')}
            </Button>

            {/* 返回登录（仅忘记密码模式） */}
            {mode === 'forgot' && (
              <div className="mt-4 text-center">
                <button
                  type="button"
                  className="min-h-11 text-sm text-[var(--text-muted)] hover:text-[var(--text-soft)] focus:underline"
                  onClick={handleSwitchToLogin}
                  disabled={isLoading}
                >
                  ← 返回登录
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  )
}

export default LoginModal
